import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import Task from '../types/Task';
import { useGetActualSpaceId, useGetUsersWithWorkspaceAccess, useGetWorkspace } from '../serverState/workspace';
import { useGetTasks, useTaskByNumber } from '../serverState/tasks';
import { useCurrentWorkspaceEpics } from '../serverState/epics';
import { TaskStatus, defaultTaskStatuses } from '../types/TaskStatus';
import { User } from '../types/User';
import { Epic } from '../types/Epic';
import { SprintStatusType } from '../components/UI/SprintStatusLabel';
import { WorkspaceFramework } from '../types/Workspace';
import { usePlanningSession } from '../serverState/planning';
import { getPossibleSprintStatuses, getEffectiveSprintStatus } from '../utils/taskListUtils';

export const useTaskDetails = (taskId: number) => {
  const workspaceQuery = useGetWorkspace();
  const actualSpaceId = useGetActualSpaceId();
  const tasksQuery = useGetTasks(Number(actualSpaceId));
  // Task View used to find its task by scanning the full space list, so a cold open
  // blocked on a multi-MB download (#3010). The space id here is the PUBLIC id from
  // the URL so this request starts before the workspace lookup resolves.
  const { spaceId: spacePublicId } = useParams();
  const taskDetailQuery = useTaskByNumber(spacePublicId, taskId);
  const epicsQuery = useCurrentWorkspaceEpics();
  const usersWithAccessToSpaceQuery = useGetUsersWithWorkspaceAccess(Number(actualSpaceId));
  const planningSessionQuery = usePlanningSession();

  const taskStatuses: TaskStatus[] = (workspaceQuery.data?.TaskStatus as TaskStatus[]) || defaultTaskStatuses;

  const [task, setTask] = useState<Task | null>(null);
  const [usersWithAccessToSpace, setUsersWithAccessToSpace] = useState<User[]>([]);
  const [epics, setEpics] = useState<Epic[]>([]);
  const [parentTaskOptions, setParentTaskOptions] = useState<Task[]>([]);

  const framework = workspaceQuery.data?.framework;
  const activeSprintId = workspaceQuery.data?.activeSprintId;
  const planningSessionId = planningSessionQuery?.data?.session?.id
    ? Number(planningSessionQuery.data.session.id)
    : null;

  const isScrum = framework === WorkspaceFramework.scrum;
  const sprintStatuses: SprintStatusType[] = getPossibleSprintStatuses(isScrum, activeSprintId).map(
    (o) => o.sprintStatus
  );

  // The sprint module scopes tasks by sprintId (not stage). Derive the displayed
  // stage from the same authoritative membership so Task View, the Stage column and
  // the Sprint module never disagree — this covers both a task with
  // stage='backlog' but sprintId===activeSprintId (shown as Sprint, #2581) and a
  // stale stage='sprint' with a non-active sprintId (shown as Backlog, #2598).
  const sprintStatus: SprintStatusType = getEffectiveSprintStatus({
    task: { stage: task?.stage, sprintId: task?.sprintId },
    isScrum,
    activeSprintId,
  });

  useEffect(() => {
    // Detail record first — it is complete and revalidated on every mount.
    if (taskDetailQuery.data?.taskId === taskId) {
      setTask(taskDetailQuery.data);
      return;
    }

    // Fallbacks for the in-flight window, and for epics, whose numbers live in the
    // epics cache rather than the task list.
    if (tasksQuery.data) {
      const updatedTask = tasksQuery.data.find((currTask) => currTask.taskId === taskId);
      if (updatedTask) {
        setTask(updatedTask);
        return;
      }
    }

    if (epicsQuery.data) {
      const foundEpic = epicsQuery.data.find((epic) => epic.taskId === taskId);
      if (foundEpic) {
        setTask(foundEpic);
        return;
      }
    }

    // Not in any loaded source. Opening a notification for another space navigates
    // before the persisted space store switches, so these queries still hold the
    // previous space's tasks — without this, TaskView keeps painting the
    // previously-opened task until the new data arrives (#2796). Only drop a task
    // that belongs to a different taskId, so a transient empty refetch of the
    // current task does not blank the view.
    setTask((current) => (current && current.taskId !== taskId ? null : current));
  }, [taskDetailQuery.data, tasksQuery.data, epicsQuery.data, taskId]);

  useEffect(() => {
    if (epicsQuery.data) {
      setEpics(epicsQuery.data);
    }
    if (tasksQuery.data) {
      setParentTaskOptions(
        tasksQuery.data?.filter((currTask) => !currTask.parentTaskId && currTask.stage !== 'triage') || []
      );
    }

    if (usersWithAccessToSpaceQuery.data) {
      setUsersWithAccessToSpace(usersWithAccessToSpaceQuery.data.map((userAccess) => userAccess.User));
    }
  }, [epicsQuery.data, tasksQuery.data, usersWithAccessToSpaceQuery.data]);

  return {
    task,
    setTask,
    epics,
    parentTaskOptions,
    usersWithAccessToSpace,
    taskStatuses,
    sprintStatuses,
    sprintStatus,
    framework,
    activeSprintId,
    planningSessionId,
  };
};
